import { Typography, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import StatusDot from '~components/icons/statusDot';
import VectorImage from '~components/media/vectorImage';
import TooltipStyle from '~components/tooltips/enums/tooltipStyle';
import Tooltip from '~components/tooltips/tooltip';
import { StatusVariant } from '~enums';
import { Dimens } from '~themes';

interface LastOnlineProps {
	date?: Dayjs | undefined;
}

const LastOnline = ({ date }: LastOnlineProps) => {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const msSinceLastReport = dayjs().diff(date);
	const dotVariant =
		msSinceLastReport == undefined ? StatusVariant.Neutral
		: msSinceLastReport <= 3_600_000 ? StatusVariant.Success
		: msSinceLastReport <= 86_400_000 ? StatusVariant.Warning
		: StatusVariant.Destructive;

	if (!date || !isDayjs(date)) {
		return (
			<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
				<StatusDot variant={StatusVariant.Neutral} />
				<Typography variant='paragraphSm'>{t('general.unknown', { ns: 'entities' })}</Typography>
			</Stack>
		);
	}

	return (
		<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
			<StatusDot variant={dotVariant} />
			<Typography variant='paragraphSm' textAlign='center' color={palette.textQuaternary.main}>
				{t('lastOnlineOn', { ns: 'system', date: date?.fromNow() })}
			</Typography>
			<Tooltip
				content={
					<Stack sx={{ padding: Dimens.spacingLg }} direction='column' spacing={Dimens.spacingMd}>
						<Typography variant='label'>
							{t('connectionStatusIndicatorsTitle', { ns: 'system' })}
						</Typography>
						<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
							<StatusDot variant={StatusVariant.Success} />
							<Typography variant='paragraphSm'>
								{t('onlineInTimeSpan', {
									ns: 'system',
									timeSpan: t('hour.thePast', {
										ns: 'system',
										count: 1,
									}),
								})}
							</Typography>
						</Stack>
						<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
							<StatusDot variant={StatusVariant.Warning} />
							<Typography variant='paragraphSm'>
								{t('onlineInTimeSpan', {
									ns: 'system',
									timeSpan: t('hour.thePast', {
										ns: 'system',
										count: 24,
									}),
								})}
							</Typography>
						</Stack>
						<Stack direction='row' alignItems='center' spacing={Dimens.spacingXs}>
							<StatusDot variant={StatusVariant.Destructive} />
							<Typography variant='paragraphSm'>
								{t('offlineForMoreThanTimeSpan', {
									ns: 'system',
									timeSpan: t('hour.hours', {
										ns: 'system',
										count: 24,
									}),
								})}
							</Typography>
						</Stack>
					</Stack>
				}
				style={TooltipStyle.Extended}
			>
				<VectorImage
					name='icon/info-circle'
					color={palette.borderPrimarySolid.main}
					height={16}
					width={16}
					style={{ verticalAlign: 'middle' }}
				/>
			</Tooltip>
		</Stack>
	);
};

export default LastOnline;
